import { combineReducers, configureStore, isAnyOf } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { dataApi } from "@/services/apis/data";
import { api } from "@/services/apis/api.ts";
import workspaceSliceReducer, {
    setSelectedWorkspace,
    workspaceUnavailable,
} from "@/features/Workspace/Workspace.slice.ts";
import { createSharedReducers } from "@/app/sharedReducers.ts";
import { createListenerMiddleware } from "@reduxjs/toolkit";
import { persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/es/storage";

const createRootReducer = () =>
    combineReducers({
        [dataApi.reducerPath]: dataApi.reducer,
        ...createSharedReducers(),
        workspace: persistReducer(
            {
                key: "workspace",
                storage,
            },
            workspaceSliceReducer,
        ),
    });

const invalidateWorkspaceDataListener = createListenerMiddleware();

invalidateWorkspaceDataListener.startListening({
    matcher: isAnyOf(
        setSelectedWorkspace,
        workspaceUnavailable,
        api.endpoints.getWorkspaces.matchFulfilled,
        api.endpoints.updateWorkspace.matchFulfilled,
    ),
    effect: async (action, listenerApi) => {
        const prevState = listenerApi.getOriginalState() as RootState;
        const currentState = listenerApi.getState() as RootState;

        // Only invalidate if the workspace actually changed
        if (
            prevState.workspace.selectedWorkspace &&
            prevState.workspace.selectedWorkspace.id !==
                currentState.workspace.selectedWorkspace?.id
        ) {
            // Discard cached data even when the selection is cleared.
            listenerApi.dispatch(api.util.resetApiState());
            listenerApi.dispatch(dataApi.util.resetApiState());

            if (workspaceUnavailable.match(action)) {
                // useSelectedWorkspace() throws without a selection, so start
                // over rather than re-render without one. Persist the cleared
                // selection first, or the reload restores the stale one.
                await persistor.flush();
                window.location.reload();
            }
        }
    },
});

export const store = configureStore({
    reducer: createRootReducer(),
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({ serializableCheck: false })
            .concat(dataApi.middleware)
            .concat(api.middleware)
            .concat(invalidateWorkspaceDataListener.middleware),
});

export type RootState = ReturnType<typeof store.getState>;

export const persistor = persistStore(store);

setupListeners(store.dispatch);
